/**
 * TransactionCard (BUILD_SPEC.md §10.3, §16.2; v1.1 B1 redesign).
 *
 * Visual states layered on top of the default card chrome:
 *   • Flash window (lastChangedAt < 30 s): the card slides in from the
 *     top. For a APPROVAL/REJECTION match — detected by
 *     `detectFlashKeyword` on actions[0].finalAction — the whole-card
 *     background swaps to Mint (#A7F3D0 light / #064E3B dark) or Rose
 *     (#FECACA / #7F1D1D). Other newly-changed cards keep the §15.2
 *     blue pulse-tint fallback.
 *   • Stripe window (30 s ≤ lastChangedAt < 5 min): the card returns
 *     to its normal surface but a 4 px colored stripe stays on the
 *     leading (RTL: right) edge — green for APPROVAL, red for
 *     REJECTION. After 5 min the stripe disappears entirely.
 *   • Stale (NOT_FOUND): yellow warning, "غير موجودة في النظام".
 *   • Paused: dimmed overlay with "متوقفة مؤقتاً".
 *
 * v1.1 B1 changes vs. v1.0:
 *   • تكليف (ASSIGNMENT) no longer drives ANY card-level visual
 *     treatment. The server still classifies it via `detectKeyword` so
 *     the daily summary and the B5 takleef flip can rely on
 *     `Action.containsKeyword === "ASSIGNMENT"`, but the card itself
 *     stays neutral.
 *   • Detection uses `detectFlashKeyword` (literal substring match on
 *     "موافقة" / "عدم موافقة", no Arabic normalization) instead of the
 *     server-pre-classified `containsKeyword`. Tuned per Omar's
 *     2026-05-13 clarification: "الموافقة" counts as APPROVAL (substring
 *     match within the article-prefixed form); "موافق" alone does not.
 *   • The thick red full-card REJECTION border is gone; the new Rose
 *     wash + red stripe carry the signal.
 *   • The corner keyword badges (CheckCircle / ClipboardList /
 *     AlertTriangle) are removed here — B8 reintroduces redesigned
 *     filled-circle icons at the start of each action row instead of
 *     the card corner.
 *
 * `prefers-reduced-motion` is honored by `flash-active`'s CSS rule
 * (see `styles/index.css`) — the pulse + glow loop is replaced by a
 * single 200 ms opacity fade. The new Mint/Rose flash uses the
 * `motion-safe:` slide-in variant for the same reason.
 */

import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Pause, ExternalLink, RotateCcw } from "lucide-react";
import type { Action, Transaction } from "@/api/types";
import { detectFlashKeyword, type FlashKeywordType } from "../../../../packages/keywords/index.js";
import { ActionRow } from "./ActionRow";
import { minutesSince } from "@/lib/dates";

/** §16.2 / v1.1 B1 — the 30 s flash window. */
const FLASH_WINDOW_MIN = 30 / 60;
/** §16.2 / v1.1 B1 — the 5 min stripe window. After this the card is
 *  visually indistinguishable from a long-untouched one. */
const STRIPE_WINDOW_MIN = 5;

/** Pulse-RGB for the §15.2 blue fallback (non-keyword newly-changed
 *  cards). APPROVAL/REJECTION cards use solid Mint/Rose via Tailwind
 *  classes instead of the CSS-variable pulse, so they don't appear
 *  here. */
const PULSE_DEFAULT_RGB = { light: "59, 130, 246", dark: "96, 165, 250" };

interface Props {
  transaction: Transaction;
  /** Latest 3 actions, position-desc. Empty array when no history yet. */
  actions: Action[];
}

/** v1.1 B1 — card flash classifier reads actions[0].finalAction (the
 *  current latest action). When that text doesn't carry "موافقة" or
 *  "عدم موافقة", the card returns null and the default chrome wins. */
function pickFlashKeyword(actions: Action[]): FlashKeywordType | null {
  return detectFlashKeyword(actions[0]?.finalAction ?? null);
}

/**
 * v1.1.1 (2026-05-14) -- status pill colors. Exact string equality on
 * `Transaction.requestStatus`, the "حالة الطلب" label from the top
 * "معلومات الطلب" block of the municipality result page. This is a
 * transaction-level field, distinct from the per-action حالة الطلب column.
 * Three signal colors plus a neutral fall-through; sentence-like values
 * ("قيد الدراسة", "قيد الاجراء") land on neutral intentionally.
 */
function pillColors(status: string): string {
  if (status === "مكتمل") return "bg-mint dark:bg-mint-dark text-fg dark:text-fg-dark";
  if (status === "مرفوض") return "bg-rose dark:bg-rose-dark text-fg dark:text-fg-dark";
  if (status === "مكلف") return "bg-peach dark:bg-peach-dark text-fg dark:text-fg-dark";
  return "bg-hairline/40 dark:bg-hairline-dark/60 text-fg dark:text-fg-dark";
}

export function TransactionCard({ transaction, actions }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [flipped, setFlipped] = useState(false);

  // v1.1 B5: takleef flip is available only when the latest action's
  // finalAction contains "تكليف" AND the scraper has persisted the
  // three takleef fields. Either side missing means the back face has
  // nothing useful to show and we suppress the affordance entirely.
  const hasTakleef =
    actions[0]?.finalAction?.includes("تكليف") === true &&
    (transaction.assigningParty !== null ||
      transaction.assigningNotes !== null ||
      transaction.assigningConfirmUrl !== null);

  const flashKeyword = useMemo(() => pickFlashKeyword(actions), [actions]);
  const minutesSinceChange = minutesSince(transaction.lastChangedAt);
  const isFlashing = minutesSinceChange < FLASH_WINDOW_MIN;
  // The colored stripe persists from the end of the flash through the
  // 5-min mark. Grid's sort lift uses the same 5-min window so the card
  // physically stays at the top while the stripe is visible.
  const isInStripeWindow =
    flashKeyword !== null && minutesSinceChange < STRIPE_WINDOW_MIN;

  // Compose state-driven classes. Base chrome → flash background
  // override → animations. Surface bg is mint/rose ONLY during the
  // 30 s flash window for a keyword card; otherwise it stays at the
  // normal surface so the stripe carries the signal alone.
  const surface =
    isFlashing && flashKeyword === "APPROVAL"
      ? "bg-mint dark:bg-mint-dark"
      : isFlashing && flashKeyword === "REJECTION"
        ? "bg-rose dark:bg-rose-dark"
        : "bg-surface dark:bg-surface-dark";

  // v1.1 B4: the card body navigates to /transactions/:id on click.
  // We add `cursor-pointer` for the affordance and a focus ring for
  // keyboard accessibility. The role-button + onClick let activation
  // also fire via Enter/Space when the card is focused.
  const classes: string[] = [
    "relative overflow-hidden rounded-xl border border-hairline dark:border-hairline-dark p-4 shadow-sm transition-colors cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-pulse focus:ring-offset-2 focus:ring-offset-canvas dark:focus:ring-offset-canvas-dark",
    surface,
  ];
  if (transaction.status === "PAUSED") {
    classes.push("opacity-70");
  }
  if (isFlashing) {
    if (flashKeyword) {
      // Mint/Rose cards still slide in but skip the v1.0 background-
      // pulse + glow keyframes — the solid wash already carries the
      // attention cue, and stacked pulses on a saturated bg flicker
      // unpleasantly on a TV viewed from 2-3 m away.
      classes.push("motion-safe:animate-slide-in");
    } else {
      // Non-keyword newly-changed card: keep the §15.2 blue pulse
      // fallback so staff still get a "something changed" signal.
      classes.push("flash-active");
    }
  }

  // `--flash-rgb` only drives the blue fallback animation; mint/rose
  // cards never set the variable.
  const isDark =
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const flashStyle: CSSProperties =
    isFlashing && !flashKeyword
      ? ({
          "--flash-rgb": isDark ? PULSE_DEFAULT_RGB.dark : PULSE_DEFAULT_RGB.light,
        } as CSSProperties)
      : {};

  const openDetail = () => navigate(`/transactions/${transaction.id}`);

  // Front face: existing card chrome. The article's role-button +
  // onClick stay on the FRONT face only so the back face's click area
  // never accidentally navigates.
  const frontFace = (
    <article
      className={`flipper-front ${classes.join(" ")}`}
      style={flashStyle}
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openDetail();
        }
      }}
      aria-label={transaction.ownerName ?? transaction.requestNumber}
    >
      {/* v1.1 B1 leading-edge stripe. Visible until the 5-min mark; the
          `start-0` utility flips with <html dir>, so the stripe sits on
          the right in Arabic (RTL) and on the left in English (LTR). */}
      {isInStripeWindow && flashKeyword === "APPROVAL" ? (
        <span
          className="absolute inset-y-0 start-0 w-1 bg-approval dark:bg-approval-dark"
          aria-hidden
        />
      ) : null}
      {isInStripeWindow && flashKeyword === "REJECTION" ? (
        <span
          className="absolute inset-y-0 start-0 w-1 bg-rejection dark:bg-rejection-dark"
          aria-hidden
        />
      ) : null}

      <header className="mb-3 pe-8">
        {/* Owner name dominant; falls back to request number when the
            scraper hasn't filled it in yet. The v1.1 B2 status pill
            sits on the same row, separated by `gap-2` so the pill
            keeps its own bidi context and doesn't crowd the name. */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="rtl-isolate text-lg font-bold text-fg dark:text-fg-dark truncate">
            {transaction.ownerName ?? transaction.requestNumber}
          </h3>
          {transaction.requestStatus ? (
            <span
              className={`rtl-isolate shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-bold ${pillColors(transaction.requestStatus)}`}
              aria-label={transaction.requestStatus}
              title={transaction.requestStatus}
            >
              {transaction.requestStatus}
            </span>
          ) : null}
        </div>
        <div className="rtl-isolate text-xs text-muted dark:text-muted-dark">
          {transaction.requestNumber} · {transaction.year}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="rtl-isolate inline-flex items-center rounded-full bg-hairline/40 dark:bg-hairline-dark/60 px-2 py-0.5 text-xs font-medium text-fg dark:text-fg-dark">
            {transaction.region}
          </span>
          <span className="rtl-isolate inline-flex items-center rounded-full bg-hairline/40 dark:bg-hairline-dark/60 px-2 py-0.5 text-xs font-medium text-fg dark:text-fg-dark">
            {transaction.serviceType}
          </span>
        </div>
      </header>

      {/* Stale and paused banners are mutually exclusive lifecycle
          states; they replace the action history with status copy. */}
      {transaction.status === "STALE" ? (
        <div className="rounded-md bg-assignment-bg dark:bg-assignment-dark-bg/40 p-3 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-assignment dark:text-assignment-dark" aria-hidden />
          <span className="text-fg dark:text-fg-dark">{t("dashboard.stale")}</span>
        </div>
      ) : transaction.status === "PAUSED" ? (
        <div className="rounded-md bg-hairline/40 dark:bg-hairline-dark/60 p-3 text-sm flex items-center gap-2">
          <Pause className="h-4 w-4 text-muted dark:text-muted-dark" aria-hidden />
          <span className="text-muted dark:text-muted-dark">{t("dashboard.paused")}</span>
        </div>
      ) : (
        <div>
          {actions.length === 0 ? (
            <p className="text-sm italic text-muted dark:text-muted-dark">{t("dashboard.noActions")}</p>
          ) : (
            actions.slice(0, 3).map((a) => <ActionRow key={a.id} action={a} />)
          )}
        </div>
      )}

      <footer className="mt-3 pt-2 border-t border-hairline dark:border-hairline-dark text-xs text-muted dark:text-muted-dark flex items-center justify-between gap-2">
        <span>
          {t("dashboard.lastUpdate")}:{" "}
          {transaction.lastChangedAt ? renderRelative(minutesSinceChange) : "—"}
        </span>
        {hasTakleef ? (
          <button
            type="button"
            onClick={(e) => {
              // Block the card's parent onClick that would otherwise
              // navigate to the detail page on the same gesture.
              e.stopPropagation();
              setFlipped(true);
            }}
            className="rounded-md border border-hairline dark:border-hairline-dark px-2 py-1 text-xs font-medium text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            {t("takleef.viewMore")}
          </button>
        ) : null}
      </footer>
    </article>
  );

  // Back face: takleef details. Only constructed when hasTakleef is
  // true so an empty back face never ends up in the DOM tree of a
  // non-takleef card.
  const backFace = hasTakleef ? (
    <article
      className={`flipper-back overflow-hidden rounded-xl border border-hairline dark:border-hairline-dark p-4 shadow-sm ${surface}`}
    >
      <header className="mb-3">
        <h3 className="rtl-isolate text-lg font-bold text-fg dark:text-fg-dark">
          {transaction.ownerName ?? transaction.requestNumber}
        </h3>
        <div className="rtl-isolate text-xs text-muted dark:text-muted-dark">
          {transaction.requestNumber} · {transaction.year}
        </div>
      </header>

      <dl className="space-y-3 text-sm">
        {transaction.assigningParty ? (
          <div>
            <dt className="text-xs text-muted dark:text-muted-dark">{t("takleef.party")}</dt>
            <dd className="rtl-isolate text-fg dark:text-fg-dark">{transaction.assigningParty}</dd>
          </div>
        ) : null}
        {transaction.assigningNotes ? (
          <div>
            <dt className="text-xs text-muted dark:text-muted-dark">{t("takleef.notes")}</dt>
            <dd className="rtl-isolate text-fg dark:text-fg-dark">{transaction.assigningNotes}</dd>
          </div>
        ) : null}
        {transaction.assigningConfirmUrl ? (
          <div>
            {/* `target="_blank" rel="noopener"` and a textual hint
                explicit-state the contract: clicking goes to the
                municipality site. The dashboard never submits a
                confirmation on the user's behalf — that decision is
                always made on Confirm_Assigning.aspx. */}
            <a
              href={transaction.assigningConfirmUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-3 py-1.5 text-sm font-semibold"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
              {t("takleef.openConfirm")}
            </a>
            <p className="mt-1 text-xs text-muted dark:text-muted-dark">
              {t("takleef.linkHint")}
            </p>
          </div>
        ) : null}
      </dl>

      <footer className="mt-3 pt-2 border-t border-hairline dark:border-hairline-dark flex items-center justify-end">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setFlipped(false);
          }}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
          {t("takleef.back")}
        </button>
      </footer>
    </article>
  ) : null;

  // When there's no takleef section we render the front face directly
  // — keeps the DOM lean and avoids the perspective wrapper for the
  // 99 % of cards that don't have a back face.
  if (!hasTakleef) return frontFace;

  return (
    <div className="flipper">
      <div className={`flipper-inner ${flipped ? "is-flipped" : ""}`}>
        {frontFace}
        {backFace}
      </div>
    </div>
  );

  // Locale-aware "X minutes ago" string. Lightweight bucket logic so we
  // don't pull in the heavy `formatDistance` machinery from date-fns.
  // Inlined as a closure so it can call `t` directly without dragging
  // i18next's TFunction type through the signature.
  function renderRelative(minutes: number): string {
    if (minutes < 1) return t("common.justNow");
    if (minutes < 60) {
      const count = Math.floor(minutes);
      return count === 1
        ? t("common.minutesAgo", { count })
        : t("common.minutesAgoPlural", { count });
    }
    if (minutes < 60 * 24) return t("common.hoursAgo", { count: Math.floor(minutes / 60) });
    return t("common.daysAgo", { count: Math.floor(minutes / (60 * 24)) });
  }
}
